/**
 * DropGen API Client
 * All API calls for DropGen UI
 */

const API_BASE = process.env.NODE_ENV === 'production'
  ? '/api/dropgen'
  : 'http://localhost:8780/api/dropgen';

export interface ResearchParameters {
  max_products?: number;
  min_score?: number;
  max_price?: number;
}

export interface StartResearchResponse {
  research_id: string;
  status: string;
  niche: string;
}

export interface Research {
  id: string;
  niche: string;
  status: 'pending' | 'researching' | 'completed' | 'failed';
  progress: number;
  parameters: ResearchParameters;
  created_at: string;
  completed_at?: string;
  results?: any;
}

export interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: any;
  created_at?: string;
}

export interface Product {
  id?: string;
  research_id?: string;
  rank: number;
  product_name: string;
  score: number;
  cost: number;
  margin: number;
  trend: string;
  profit_potential: string;
  estimated_monthly_revenue: string;
  quick_wins: string[];
  full_analysis?: any;
}

export interface ProductFilters {
  research_id?: string;
  min_score?: number;
  max_price?: number;
  limit?: number;
}

export interface CostBreakdown {
  productCost: number;
  shipping: {
    tier: 'light' | 'medium' | 'heavy';
    estimate: number;
    range: string;
    days: string;
    note: string;
  };
  platformFees: number;
  marketing: number;
  returns: number;
  totalCosts: number;
}

export interface SearchProduct {
  cj_pid: string;
  sku: string;
  name: string;
  image: string | null;
  product_url: string;
  category: string;
  cost: number;
  retail: number;
  netProfit: number;
  margin: number;
  roi: number;
  breakdown: CostBreakdown | null;
  listedNum: number;
  hasVideo: boolean;
  supplierName: string | null;
  stock: number;
  _raw?: Record<string, unknown>;
}

export interface SearchResponse {
  products: SearchProduct[];
  total: number;
  page: number;
  hasMore: boolean;
  query?: string;
  error?: string;
}

export interface Trend {
  keyword: string;
  search_volume: number;
  trend_direction: 'up' | 'down' | 'stable';
  competition: 'low' | 'medium' | 'high';
}

class APIError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'APIError';
  }
}

async function fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  try {
    const url = `${API_BASE}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new APIError(response.status, error.error || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    // Network error or fetch failed
    console.error('[API Error]', endpoint, error);
    throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown'}`);
  }
}

// Research API

export async function startResearch(
  niche: string,
  parameters: ResearchParameters = {}
): Promise<StartResearchResponse> {
  return fetchAPI('/research', {
    method: 'POST',
    body: JSON.stringify({ niche, parameters }),
  });
}

export interface SavedProduct {
  id: string;
  cj_product_id: string;
  title: string;
  category: string;
  cost_usd: number;
  images: string[];
  variants: Record<string, unknown>;
  fetched_at?: string;
}

export async function startProductResearch(
  product: SavedProduct
): Promise<StartResearchResponse> {
  return fetchAPI('/research', {
    method: 'POST',
    body: JSON.stringify({
      product: {
        id: product.id,
        name: product.title,
        cj_product_id: product.cj_product_id,
        cost_usd: product.cost_usd,
        category: product.category,
        image: product.images?.[0] || null,
        retail_price: (product.variants as { retail_price?: number })?.retail_price || product.cost_usd * 3
      }
    }),
  });
}

export async function getResearch(researchId: string): Promise<Research> {
  return fetchAPI(`/research/${researchId}`);
}

export interface ResearchSummary {
  id: string;
  niche: string;
  status: string;
  created_at: string;
  completed_at?: string;
  processing_time_ms?: number;
  products_count?: number;
  opportunity_score?: number;
  recommendation?: string;
}

export async function getResearchHistory(status?: string): Promise<{ count: number; research: ResearchSummary[] }> {
  const endpoint = status ? `/research?status=${status}` : '/research';
  return fetchAPI(endpoint);
}

export async function clearResearchHistory(): Promise<{ message: string; deleted: number }> {
  return fetchAPI('/research/history', {
    method: 'DELETE',
  });
}

export async function deleteResearch(researchId: string): Promise<{ message: string }> {
  return fetchAPI(`/research/${researchId}`, {
    method: 'DELETE',
  });
}

// Chat API

export async function sendMessage(
  researchId: string,
  message: string
): Promise<ChatMessage> {
  return fetchAPI('/chat', {
    method: 'POST',
    body: JSON.stringify({ research_id: researchId, message }),
  });
}

export async function getChatHistory(researchId: string): Promise<{ research_id: string; messages: ChatMessage[] }> {
  return fetchAPI(`/chat/history/${researchId}`);
}

// Products API

export async function getProducts(filters: ProductFilters = {}): Promise<{ count: number; filters: ProductFilters; products: Product[] }> {
  const params = new URLSearchParams();

  if (filters.research_id) params.append('research_id', filters.research_id);
  if (filters.min_score !== undefined) params.append('min_score', filters.min_score.toString());
  if (filters.max_price !== undefined) params.append('max_price', filters.max_price.toString());
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());

  const query = params.toString();
  const endpoint = query ? `/products?${query}` : '/products';

  return fetchAPI(endpoint);
}

export async function getProduct(productId: string): Promise<Product> {
  return fetchAPI(`/products/${productId}`);
}

// Search API

export async function searchProducts(query: string, page = 1, limit = 25): Promise<SearchResponse> {
  const params = new URLSearchParams({ q: query, page: String(page), limit: String(limit) });
  return fetchAPI(`/search?${params}`);
}

export async function saveProduct(product: SearchProduct): Promise<{ message: string; product_id?: string }> {
  return fetchAPI('/products', {
    method: 'POST',
    body: JSON.stringify(product),
  });
}

// Trends API

export async function getTrends(filters: { limit?: number } = {}): Promise<{ trends: Trend[] }> {
  const params = new URLSearchParams();
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());

  const query = params.toString();
  const endpoint = query ? `/trends?${query}` : '/trends';

  return fetchAPI(endpoint);
}

// Trends V2 API (Rust-powered)

export interface TrendAnalysis {
  keyword: string;
  trend_score?: number;
  interest_score?: number;
  direction?: 'Rising' | 'Falling' | 'Stable';
  velocity?: number;
  maturity?: 'Emerging' | 'Peak' | 'Stable' | 'Declining';
  confidence?: number;
  category?: string;
  traffic?: { min: number; max: number };
  matched_products?: Array<{
    cj_product_id: string;
    product_name: string;
    match_score: number;
  }>;
  chart_data?: {
    labels: string[];
    values: number[];
    moving_avg_7d: number[];
    moving_avg_30d: number[];
  };
  processing_time_ms?: number;
  fetched_at?: string;
}

export async function analyzeTrend(keyword: string, geo = 'US'): Promise<TrendAnalysis> {
  return fetchAPI('/trends/v2/analyze', {
    method: 'POST',
    body: JSON.stringify({ keyword, geo }),
  });
}

export async function getTrendingNow(geo = 'US'): Promise<{ trends: TrendAnalysis[] }> {
  return fetchAPI(`/trends/v2/now?geo=${geo}`);
}

// Matcher API (Rust-powered)

export interface MatchResult {
  cj_product_id: string;
  cj_product_name: string;
  product_url: string;
  confidence: number;
  match_type: 'exact' | 'similar' | 'variant' | 'related';
  signals: {
    title_similarity: number;
    image_similarity?: number;
    price_alignment: number;
    category_match: number;
    spec_overlap: number;
    rag_boost: number;
  };
  price_comparison: {
    source_price: number | null;
    cj_price: number;
    markup_ratio: number | null;
    in_expected_range: boolean;
  };
}

export interface MatcherResponse {
  matches: MatchResult[];
  processing_time_ms: number;
}

export async function findMatches(
  productName: string,
  source: 'amazon' | 'ebay' | 'aliexpress' | 'manual',
  options?: { price?: number; category?: string; image?: string }
): Promise<MatcherResponse> {
  return fetchAPI('/matcher/find', {
    method: 'POST',
    body: JSON.stringify({
      product_name: productName,
      source,
      price: options?.price,
      category: options?.category,
      product_image: options?.image,
    }),
  });
}

// Discovery API (Rust processor-powered auto-discovery)

export interface DiscoveredProduct {
  cj_pid: string;
  sku: string | null;
  name: string;
  image: string | null;
  product_url: string;
  cost: number;
  score: number;
  profit_margin: number;
  roi: number;
  net_profit: number;
  retail: number;
  listed_num: number;
  has_video: boolean;
  category: string;
  supplier: string | null;
}

export interface DiscoveryResponse {
  products: DiscoveredProduct[];
  keywords: string[];
  total: number;
  processing_time_ms: number;
  cached: boolean;
  message?: string;
  error?: string;
}

export async function discoverProducts(limit = 50, refresh = false): Promise<DiscoveryResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (refresh) params.append('refresh', 'true');
  return fetchAPI(`/discover?${params}`);
}

export async function discoverCustom(keywords: string[]): Promise<DiscoveryResponse> {
  return fetchAPI('/discover/custom', {
    method: 'POST',
    body: JSON.stringify({ keywords }),
  });
}

// ============================================
// Velocity API - Hot Product Tracking
// ============================================

export interface VelocityData {
  tracked: boolean;
  velocity: number | null;
  trend: 'hot' | 'warm' | 'slow' | 'dead' | 'unknown';
  label: string | null;
  stock: number | null;
  last_updated: string | null;
}

export async function checkVelocity(cjPid: string): Promise<VelocityData> {
  try {
    const res = await fetch(`${API_BASE}/velocity/check/${cjPid}`);
    if (!res.ok) return { tracked: false, velocity: null, trend: 'unknown', label: null, stock: null, last_updated: null };
    return await res.json();
  } catch {
    return { tracked: false, velocity: null, trend: 'unknown', label: null, stock: null, last_updated: null };
  }
}

export async function getHotProducts(limit = 20): Promise<{ products: Array<{ cj_pid: string; velocity: number; trend: string; label: string }>; count: number }> {
  return fetchAPI(`/velocity/hot?limit=${limit}`);
}

// Health check

export async function checkHealth(): Promise<{ status: string; service: string; timestamp: string; clients: number }> {
  const url = process.env.NODE_ENV === 'production'
    ? '/health'
    : 'http://localhost:8780/health';

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Health check failed: ${response.status}`);
  }
  return await response.json();
}

// ============================================
// AutoStore API - Shopify Publishing Pipeline
// ============================================

const AUTOSTORE_BASE = process.env.NODE_ENV === 'production'
  ? '/api/autostore'
  : 'http://localhost:8780/api/autostore';

async function fetchAutoStore<T>(endpoint: string, options?: RequestInit): Promise<T> {
  try {
    const url = `${AUTOSTORE_BASE}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new APIError(response.status, error.error || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    console.error('[AutoStore API Error]', endpoint, error);
    throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown'}`);
  }
}

// AutoStore Types

export interface StoreSettings {
  default_markup: number;
  min_margin: number;
  auto_publish: boolean;
  auto_publish_threshold: number;
}

export interface AutoStoreStore {
  id: string;
  name: string;
  shopify_domain: string;
  categories: string[];
  is_default: boolean;
  is_active: boolean;
  settings: StoreSettings;
  created_at: string;
  updated_at: string;
}

export interface StoreInput {
  name: string;
  shopify_domain: string;
  shopify_access_token: string;
  categories?: string[];
  is_default?: boolean;
  settings?: Partial<StoreSettings>;
}

export type QueueStatus =
  | 'pending'
  | 'generating'
  | 'ready'
  | 'approved'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'rejected';

export interface QueueSourceData {
  name: string;
  sku?: string;
  product_url?: string;
  image: string | null;
  images?: string[];
  category: string;
  description?: string;
  trend_score?: number;
  listed_num?: number;
  has_video?: boolean;
}

export interface QueueItem {
  id: string;
  product_id: string | null;
  cj_pid: string;
  source_data: QueueSourceData;
  store_id: string | null;
  store?: AutoStoreStore;
  status: QueueStatus;
  generated_title: string | null;
  generated_description: string | null;
  generated_tags: string[] | null;
  generated_seo_title: string | null;
  generated_seo_description: string | null;
  cj_cost: number;
  generated_price: number | null;
  price_floor: number | null;
  price_ceiling: number | null;
  pricing_confidence: number | null;
  pricing_reasoning: string | null;
  original_images: string[];
  processed_images: string[] | null;
  has_variants: boolean;
  variants: Record<string, unknown> | null;
  shopify_product_id: string | null;
  shopify_product_handle: string | null;
  shopify_admin_url?: string;
  shopify_storefront_url?: string;
  created_at: string;
  updated_at: string;
  generated_at: string | null;
  approved_at: string | null;
  published_at: string | null;
  error_message: string | null;
  retry_count: number;
}

export interface QueueProductInput {
  product_id?: string;
  cj_pid: string;
  sku?: string;
  product_url?: string;
  name: string;
  image: string;
  images?: string[];
  cost: number;
  category: string;
  variants?: Record<string, unknown>;
  trend_score?: number;
  listed_num?: number;
  has_video?: boolean;
}

// AutoStore API Functions

// Stores
export async function getAutoStoreStores(): Promise<{ success: boolean; stores: AutoStoreStore[] }> {
  return fetchAutoStore('/stores');
}

export async function createAutoStoreStore(input: StoreInput): Promise<{
  success: boolean;
  store: AutoStoreStore;
  connection_test: { success: boolean; shop_name?: string; plan?: string; error?: string };
}> {
  return fetchAutoStore('/stores', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateAutoStoreStore(id: string, updates: Partial<StoreInput>): Promise<{
  success: boolean;
  store: AutoStoreStore;
}> {
  return fetchAutoStore(`/stores/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteAutoStoreStore(id: string): Promise<{ success: boolean }> {
  return fetchAutoStore(`/stores/${id}`, { method: 'DELETE' });
}

export async function testAutoStoreConnection(id: string): Promise<{
  success: boolean;
  shop: { name: string; domain: string; plan: string; products_count: number };
}> {
  return fetchAutoStore(`/stores/${id}/test`, { method: 'POST' });
}

// Queue
export async function getAutoStoreQueue(params: {
  status?: string;
  store_id?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<{ success: boolean; items: QueueItem[]; total: number; has_more: boolean }> {
  const qs = new URLSearchParams();
  if (params.status) qs.append('status', params.status);
  if (params.store_id) qs.append('store_id', params.store_id);
  if (params.limit) qs.append('limit', String(params.limit));
  if (params.offset) qs.append('offset', String(params.offset));
  return fetchAutoStore(`/queue?${qs}`);
}

export async function addToAutoStoreQueue(products: QueueProductInput[], store_id?: string): Promise<{
  success: boolean;
  queued: number;
  items: Array<{ id: string; cj_pid: string; status: string }>;
}> {
  return fetchAutoStore('/queue/add', {
    method: 'POST',
    body: JSON.stringify({ products, store_id }),
  });
}

export async function generateAutoStoreContent(id: string): Promise<{
  success: boolean;
  item: QueueItem;
  warnings?: string[];
}> {
  return fetchAutoStore(`/queue/${id}/generate`, { method: 'POST' });
}

export async function generateAutoStoreContentBulk(ids: string[]): Promise<{
  success: boolean;
  generated: number;
  failed: number;
  results: Array<{ id: string; success: boolean; error?: string }>;
}> {
  return fetchAutoStore('/queue/generate-bulk', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export async function approveAutoStoreItem(id: string): Promise<{ success: boolean; item: QueueItem }> {
  return fetchAutoStore(`/queue/${id}/approve`, { method: 'POST' });
}

export async function approveAutoStoreItemsBulk(ids: string[]): Promise<{
  success: boolean;
  approved: number;
  items: QueueItem[];
}> {
  return fetchAutoStore('/queue/approve-bulk', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export async function rejectAutoStoreItem(id: string, reason?: string): Promise<{ success: boolean; item: QueueItem }> {
  return fetchAutoStore(`/queue/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function updateAutoStoreQueueItem(id: string, updates: {
  generated_title?: string;
  generated_description?: string;
  generated_price?: number;
  price_floor?: number;
  price_ceiling?: number;
  generated_tags?: string[];
  generated_seo_title?: string;
  generated_seo_description?: string;
  store_id?: string;
}): Promise<{ success: boolean; item: QueueItem }> {
  return fetchAutoStore(`/queue/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteAutoStoreQueueItem(id: string): Promise<{ success: boolean }> {
  return fetchAutoStore(`/queue/${id}`, { method: 'DELETE' });
}

// Publish
export async function publishAutoStoreItem(id: string): Promise<{
  success: boolean;
  item: QueueItem;
  shopify_url: string;
  shopify_product: { id: string; handle: string; variants: unknown[] };
}> {
  return fetchAutoStore(`/publish/queue/${id}`, { method: 'POST' });
}

export async function publishAutoStoreItemsBulk(ids: string[]): Promise<{
  success: boolean;
  published: number;
  failed: number;
  results: Array<{ id: string; success: boolean; shopify_id?: string; error?: string }>;
}> {
  return fetchAutoStore('/publish/bulk', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export async function getAutoStorePublished(params: {
  store_id?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<{ success: boolean; items: QueueItem[]; total: number; has_more: boolean }> {
  const qs = new URLSearchParams();
  if (params.store_id) qs.append('store_id', params.store_id);
  if (params.limit) qs.append('limit', String(params.limit));
  if (params.offset) qs.append('offset', String(params.offset));
  return fetchAutoStore(`/publish/published?${qs}`);
}

export async function unpublishAutoStoreItem(id: string): Promise<{ success: boolean; item: QueueItem }> {
  return fetchAutoStore(`/publish/published/${id}`, { method: 'DELETE' });
}

export async function getAutoStoreStats(store_id?: string): Promise<{
  success: boolean;
  stats: Record<QueueStatus, number>;
  total: number;
}> {
  const qs = store_id ? `?store_id=${store_id}` : '';
  return fetchAutoStore(`/publish/stats${qs}`);
}

export async function checkAutoStoreHealth(): Promise<{
  success: boolean;
  services: {
    api: string;
    database: string;
    ollama: string;
    ollama_models: string[];
  };
  stores_count: number;
}> {
  return fetchAutoStore('/health');
}

// Image Generation

export interface ImageSuite {
  white_background: string | null;
  lifestyle: string | null;
  hero_banner: string | null;
  instagram: string | null;
  facebook: string | null;
}

export interface ImageGenerationResult {
  success: boolean;
  suite?: ImageSuite;
  image?: string;
  type?: string;
  generated?: number;
  total?: number;
  processing_time_ms?: number;
  error?: string;
}

export interface ImageProcessorStatus {
  success: boolean;
  status: string;
  available: boolean;
  vram_free?: number;
  capabilities?: string[];
  error?: string;
}

export async function getImageProcessorStatus(): Promise<ImageProcessorStatus> {
  return fetchAutoStore('/publish/image-status');
}

export async function generateImageSuite(id: string): Promise<ImageGenerationResult> {
  return fetchAutoStore(`/publish/generate-images/${id}`, { method: 'POST' });
}

export async function generateSingleImage(
  id: string,
  type: 'lifestyle' | 'hero' | 'social' | 'collection',
  options?: {
    color_scheme?: string;
    platform?: string;
    style?: string;
  }
): Promise<ImageGenerationResult> {
  return fetchAutoStore(`/publish/generate-single/${id}`, {
    method: 'POST',
    body: JSON.stringify({ type, options }),
  });
}

// ============================================
// Opportunities API - Pre-scored deals
// ============================================

export interface Opportunity {
  id: string;
  cj_pid: string;
  name: string;
  category: string | null;
  cj_cost: number;
  images: string[] | null;
  supplier_name: string | null;
  delivery_days: number | null;
  stock: number | null;
  listed_num: number | null;
  has_video: boolean;
  cj_data: Record<string, unknown> | null;
  opportunity_score: number;
  margin_score: number;
  trend_score: number;
  competition_score: number;
  quality_score: number;
  generated_title: string | null;
  generated_description: string | null;
  generated_tags: string[] | null;
  generated_price: number | null;
  pricing_data: Record<string, unknown> | null;
  matched_store_ids: string[] | null;
  matched_categories: string[] | null;
  status: 'active' | 'queued' | 'dismissed' | 'expired';
  discovered_at: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

export async function getOpportunities(params: {
  category?: string;
  min_score?: number;
  store_id?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<{ success: boolean; opportunities: Opportunity[]; total: number; last_scan?: string }> {
  const qs = new URLSearchParams();
  if (params.category) qs.append('category', params.category);
  if (params.min_score !== undefined) qs.append('min_score', String(params.min_score));
  if (params.store_id) qs.append('store_id', params.store_id);
  if (params.limit) qs.append('limit', String(params.limit));
  if (params.offset) qs.append('offset', String(params.offset));
  return fetchAPI(`/opportunities?${qs}`);
}

export async function getOpportunity(id: string): Promise<{ success: boolean; opportunity: Opportunity }> {
  return fetchAPI(`/opportunities/${id}`);
}

export async function dismissOpportunity(id: string): Promise<{ success: boolean }> {
  return fetchAPI(`/opportunities/${id}/dismiss`, { method: 'POST' });
}

export async function queueOpportunity(id: string, storeId?: string): Promise<{ success: boolean; queue_id: string }> {
  return fetchAPI(`/opportunities/${id}/queue`, {
    method: 'POST',
    body: JSON.stringify({ store_id: storeId }),
  });
}

export async function refreshOpportunities(): Promise<{ success: boolean; message: string }> {
  return fetchAPI('/opportunities/refresh', { method: 'POST' });
}

export async function getOpportunityStats(): Promise<{
  success: boolean;
  stats: {
    total: number;
    by_category: Record<string, number>;
    avg_score: number;
    top_categories: string[];
  };
}> {
  return fetchAPI('/opportunities/stats');
}

// ============================================
// Campaigns API - Batch discovery
// ============================================

export interface Campaign {
  id: string;
  name: string;
  description: string | null;
  search_query: string;
  categories: string[] | null;
  min_price: number | null;
  max_price: number | null;
  min_margin: number;
  target_count: number;
  store_id: string | null;
  status: 'draft' | 'running' | 'completed' | 'failed';
  progress: number;
  current_task: string | null;
  products_found: number;
  products_qualified: number;
  products_generated: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignProduct {
  id: string;
  campaign_id: string;
  cj_pid: string;
  cj_data: Record<string, unknown>;
  opportunity_score: number | null;
  pricing_data: Record<string, unknown> | null;
  generated_title: string | null;
  generated_description: string | null;
  generated_tags: string[] | null;
  generated_price: number | null;
  status: 'found' | 'analyzed' | 'generated' | 'approved' | 'queued' | 'rejected';
  rejection_reason: string | null;
  created_at: string;
}

export async function getCampaigns(): Promise<{ success: boolean; campaigns: Campaign[] }> {
  return fetchAPI('/campaigns');
}

export async function getCampaign(id: string): Promise<{ success: boolean; campaign: Campaign }> {
  return fetchAPI(`/campaigns/${id}`);
}

export async function createCampaign(input: {
  name: string;
  description?: string;
  search_query: string;
  categories?: string[];
  min_price?: number;
  max_price?: number;
  min_margin: number;
  target_count: number;
  store_id?: string;
}): Promise<{ success: boolean; campaign: Campaign }> {
  return fetchAPI('/campaigns', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function startCampaign(id: string): Promise<{ success: boolean; campaign: Campaign }> {
  return fetchAPI(`/campaigns/${id}/start`, { method: 'POST' });
}

export async function cancelCampaign(id: string): Promise<{ success: boolean; campaign: Campaign }> {
  return fetchAPI(`/campaigns/${id}/cancel`, { method: 'POST' });
}

export async function deleteCampaign(id: string): Promise<{ success: boolean }> {
  return fetchAPI(`/campaigns/${id}`, { method: 'DELETE' });
}

export async function getCampaignProducts(campaignId: string): Promise<{
  success: boolean;
  products: CampaignProduct[];
  total: number;
}> {
  return fetchAPI(`/campaigns/${campaignId}/products`);
}

export async function approveCampaignProduct(campaignId: string, productId: string): Promise<{
  success: boolean;
  product: CampaignProduct;
}> {
  return fetchAPI(`/campaigns/${campaignId}/products/${productId}/approve`, { method: 'POST' });
}

export async function rejectCampaignProduct(
  campaignId: string,
  productId: string,
  reason?: string
): Promise<{ success: boolean; product: CampaignProduct }> {
  return fetchAPI(`/campaigns/${campaignId}/products/${productId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function queueApprovedCampaignProducts(campaignId: string): Promise<{
  success: boolean;
  queued: number;
  queue_ids: string[];
}> {
  return fetchAPI(`/campaigns/${campaignId}/queue-approved`, { method: 'POST' });
}

export { APIError };
